import { useMemo, useState } from 'react';
import { movies, allGenres } from '@/mocks/movies';
import SectionHeading from './SectionHeading';
import MovieCard from './MovieCard';

export default function LibraryFilter() {
  const [activeGenre, setActiveGenre] = useState('全部');
  const [query, setQuery] = useState('');

  const genreOptions = useMemo(
    () => ['全部', ...allGenres.filter((g) => movies.some((m) => m.genres.includes(g)))],
    [],
  );

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return movies.filter((movie) => {
      const matchGenre = activeGenre === '全部' || movie.genres.includes(activeGenre);
      const matchKeyword =
        keyword.length === 0 ||
        movie.title.toLowerCase().includes(keyword) ||
        movie.en.toLowerCase().includes(keyword) ||
        movie.tagline.toLowerCase().includes(keyword) ||
        movie.summary.toLowerCase().includes(keyword) ||
        movie.genres.some((g) => g.toLowerCase().includes(keyword));
      return matchGenre && matchKeyword;
    });
  }, [activeGenre, query]);

  const reset = () => {
    setActiveGenre('全部');
    setQuery('');
  };

  return (
    <section id="library" className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          kicker="Browse & Filter"
          title="片库筛选：按类型与关键词缩小范围"
          desc="所有影片都在本页之中，选择类型标签或在搜索框输入片名、剧情关键词即可即时筛选，结果随输入变化，方便你一步步锁定目标。"
        />

        <div className="mt-8 p-4 md:p-6 rounded-lg bg-background-100 border border-background-200">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative w-full lg:max-w-sm">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="搜索影片"
                placeholder="搜索片名、类型或剧情关键词…"
                className="w-full h-10 md:h-11 pl-10 pr-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs md:text-sm text-foreground-500">类型：</span>
              {genreOptions.map((genre) => {
                const active = genre === activeGenre;
                return (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => setActiveGenre(genre)}
                    className={`px-3 h-8 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                      active
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-200 text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                    }`}
                  >
                    {genre}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={reset}
              className="lg:ml-auto inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-background-300 text-xs md:text-sm text-foreground-700 whitespace-nowrap hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置
            </button>
          </div>

          <p className="mt-4 text-xs md:text-sm text-foreground-600">
            共找到 <strong className="text-primary-600 font-bold">{results.length}</strong> 部影片
            {activeGenre !== '全部' && <span>（类型：{activeGenre}）</span>}
            {query.trim() && <span>（关键词：{query.trim()}）</span>}
          </p>
        </div>

        {results.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="mt-6 py-16 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100">
            <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-film-line text-2xl" />
            </span>
            <p className="mt-4 text-sm font-medium text-foreground-700">没有符合条件的影片</p>
            <p className="mt-1 text-xs text-foreground-500">试试更换类型，或清空关键词重新查找。</p>
            <button
              type="button"
              onClick={reset}
              className="mt-4 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}